// --- MÉTODOS DE PAGAMENTO (mantidos, com normalização robusta) ---
export type DbPaymentMethod =
  | 'pix' | 'boleto' | 'card' | 'transferencia' | 'cash' | 'outro';

export function uiToDbPaymentMethod(method: string | null | undefined): DbPaymentMethod | null {
  if (!method) return null;
  const m = method.toString().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
  switch (m) {
    case 'pix': return 'pix';
    case 'boleto': return 'boleto';
    case 'cartao': return 'card';
    case 'transferencia': return 'transferencia';
    case 'especie':
    case 'dinheiro': return 'cash';
    case 'outro': return 'outro';
    default: return 'outro';
  }
}

export function dbToUiPaymentMethod(db: string | null | undefined):
  'PIX' | 'Boleto' | 'Cartão' | 'Transferência' | 'Espécie' | 'Outro' | undefined {
  switch ((db ?? '').toLowerCase()) {
    case 'pix': return 'PIX';
    case 'boleto': return 'Boleto';
    case 'card': return 'Cartão';
    case 'transferencia': return 'Transferência';
    case 'cash': return 'Espécie';
    case 'outro': return 'Outro';
    default: return undefined;
  }
}

// --- UI HELPERS (mantidos) ---
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const fmtMoney = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// =========================
//  DATAS — SEM FUSO/TZ
// =========================

// Retorna true se for "YYYY-MM-DD"
export const isDateOnly = (s: unknown): s is string =>
  typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

/** Converte Date|ISO para YYYY-MM-DD sem deslocar fuso.
 *  - Se já vier "YYYY-MM-DD", retorna como está (idempotente)
 *  - Se vier ISO com hora, usa o calendário local para montar YYYY-MM-DD
 */
export function toISODateLocal(d: Date | string): string {
  if (isDateOnly(d)) return d;
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Constrói um Date local a partir de "YYYY-MM-DD" sem UTC-parse */
export function localDateFromISODate(isoDate: string): Date {
  if (!isDateOnly(isoDate)) return new Date(isoDate);
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Converte Date para "YYYY-MM-DDTHH:mm:ss" no fuso local (sem perder o horário para UTC).
 * Equivale ao old "new Date().toISOString()" mas mantém o fuso horário local.
 * 
 * ⚠️ Não usar para salvar em DB. Use apenas para UI. Para DB use .toISOString() (UTC) ou toISODateLocal (DATE).
 */
export const toISODateTimeLocal = (date: Date = new Date()): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}:${s}`;
};

/**
 * Hoje em YYYY-MM-DD pelo calendário local (substitui o antigo todayISO baseado em UTC).
 */
export const todayISO = () => toISODateLocal(new Date());

/**
 * Constrói um Date no início do mês (local).
 */
export const startOfMonth = (d: Date = new Date()) =>
  new Date(d.getFullYear(), d.getMonth(), 1);

/**
 * Constrói um Date no fim do mês às 23:59:59.999 (local).
 */
export const endOfMonth = (d: Date = new Date()) =>
  new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);

/**
 * Retorna true se um ISO (YYYY-MM-DD ou ISO completo) cai dentro do mês de ref (respeitando calendário local).
 * Evita interpretar "YYYY-MM-DD" como UTC e escorregar um dia.
 */
export const inMonth = (iso: string, ref: Date) => {
  // Se vier "YYYY-MM-DD", montamos uma data local às 00:00.
  // Se vier ISO completo, ainda normalizamos pro dia local.
  const only = iso.length >= 10 ? iso.slice(0, 10) : toISODateLocal(iso);
  const x = new Date(only + 'T00:00:00'); // força horário local sem deslocar o dia
  return x >= startOfMonth(ref) && x <= endOfMonth(ref);
};

/**
 * Range do mês em strings YYYY-MM-DD (útil para queries gte/lte).
 */
export function monthRangeISO(ref: Date): { start: string; end: string } {
  return {
    start: toISODateLocal(startOfMonth(ref)),
    end: toISODateLocal(endOfMonth(ref)),
  };
}

// =========================
//  OUTROS HELPERS (mantidos)
// =========================

export const weekOfMonth = (date: Date) => {
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  const offset = first.getDay();
  return Math.floor((date.getDate() + offset - 1) / 7) + 1;
};

export const overlap = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) =>
  aStart < bEnd && bStart < aEnd;

/**
 * Soma horas a um ISO completo. Mantido.
 * Observação: para campos de "dia" (YYYY-MM-DD) prefira usar toISODateLocal e não adicionar horas.
 */
export const addHoursISO = (iso: string, h: number) => {
  const d = new Date(iso);
  d.setHours(d.getHours() + h);
  return d.toISOString();
};
